import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SUPPORTED_MARKETPLACES } from '../config/marketplaces';
import MarketplacePlatformPicker from '../components/MarketplacePlatformPicker';
import HelpRequestCard from "../components/HelpRequestCard";
import {
  Link2,
  Image,
  CheckCircle2,
  Loader2,
  Package,
  AlertTriangle,
} from "lucide-react";
import { MarketplaceLogo } from "../components/MarketplaceLogo";
import MarketplaceAccessPanel from "../components/MarketplaceAccessPanel";
import { toast } from "sonner";
import {
  detectPlatformFromUrl,
  previewProductUrl,
  submitProductUrl,
  validateProductUrl,
} from "../lib/urlSubmission";

const PLATFORMS = SUPPORTED_MARKETPLACES;

const CATEGORIES = [
  "Electronics",
  "Fashion",
  "Home & Garden",
  "Sports & Outdoors",
  "Beauty & Health",
  "Toys & Games",
  "Automotive",
  "Books & Media",
  "General",
  "Other",
];

export default function SubmitProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [step, setStep] = useState(1); // 1: URL input, 2: Preview/Edit, 3: Success
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [platform, setPlatform] = useState("");
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  
  // Product data
  const [title, setTitle] = useState("");
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState("General");
  const [estimatedWeight, setEstimatedWeight] = useState("");
  
  // Image upload
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (!url.trim()) return;
    const detected = detectPlatformFromUrl(url);
    if (detected !== "other") {
      setPlatform(detected);
    }
  }, [url]);
  
  const handlePreviewUrl = async () => {
    const urlError = validateProductUrl(url);
    if (urlError) {
      toast.error(urlError);
      return;
    }

    const resolvedPlatform = platform || detectPlatformFromUrl(url);
    if (!resolvedPlatform) {
      toast.error("Please select a marketplace");
      return;
    }
    if (!platform && resolvedPlatform !== "other") {
      setPlatform(resolvedPlatform);
    }

    setLoading(true);
    const result = await previewProductUrl(url, API);
    setLoading(false);

    if (result.success) {
      setPreview({
        success: true,
        url,
        title: result.title,
        image: result.images?.[0],
      });
      setTitle(result.title || "");
      if (result.images?.length) setImages(result.images);
      if (result.category) setCategory(result.category);
      if (result.estimated_weight_lb) {
        setEstimatedWeight(String(result.estimated_weight_lb));
      }
      if (result.platform && result.platform !== "other") {
        setPlatform(result.platform);
      }
      setStep(2);
      toast.success("Fresh marketplace data loaded");
      return;
    }

    setPreview({ success: false, url });
    setStep(2);
    toast.info(result.error || "Could not auto-fetch product details. Please enter them manually.");
  };

  const handleAddImage = () => {
    if (imageUrl.trim()) {
      setImages([...images, imageUrl.trim()]);
      setImageUrl("");
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim() || title.length < 3) {
      toast.error("Please enter a product title (minimum 3 characters)");
      return;
    }
    if (images.length === 0) {
      toast.error("Please add at least one product image");
      return;
    }

    setSubmitting(true);
    const result = await submitProductUrl(
      {
        source_platform: platform || detectPlatformFromUrl(url),
        source_url: url,
        title: title.trim(),
        images,
        category,
        estimated_weight_lb: estimatedWeight ? parseFloat(estimatedWeight) : null,
      },
      API
    );
    setSubmitting(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    toast.success("Product submitted for review!");
    setStep(3);
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="submit-title">
            Paste a product link — we&apos;ll deliver it with customs included.
          </h1>
          <p className="text-lg text-slate-600 mb-6">
            Browse a marketplace below, copy the product link, then paste it in the box to get started.
          </p>
          <MarketplaceAccessPanel variant="default" className="mb-6" pasteTarget="below" />
          <div className="container-card p-6 border-2 border-orange-200 bg-orange-50/50 text-center">
            <Package className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Sign in to submit your link</h2>
            <p className="text-slate-600 mb-6">
              Create a free account or sign in to preview your product and receive your all-inclusive delivered price.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() =>
                  navigate("/login", {
                    state: {
                      from: {
                        pathname: location.pathname.startsWith("/submit") ? location.pathname : "/submit",
                      },
                    },
                  })
                }
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-500 text-white font-semibold"
              >
                Sign In to Continue
              </Button>
              <Button variant="outline" onClick={() => navigate("/register")}>
                Create Account
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Headline */}
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="submit-title">
          Paste a product link — we&apos;ll deliver it to Barbados with customs included.
        </h1>
        <p className="text-lg text-slate-600 mb-6">
          Shop on Amazon, eBay, AliExpress, TEMU, SHEIN, Walmart, or Alibaba. Copy the product link and paste it here.
        </p>
        
        {/* 3 Compact Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0 font-bold">1</div>
            <div>
              <p className="font-semibold text-slate-900">Paste product link</p>
              <p className="text-sm text-slate-600">From any supported marketplace</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0 font-bold">2</div>
            <div>
              <p className="font-semibold text-slate-900">ONE final price</p>
              <p className="text-sm text-slate-600">Shipping + customs + taxes included</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0 font-bold">3</div>
            <div>
              <p className="font-semibold text-slate-900">Pay &amp; we import</p>
              <p className="text-sm text-slate-600">Delivered to your door</p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s
                    ? "bg-orange-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 h-1 mx-2 ${
                    step > s ? "bg-orange-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: marketplace first, then paste link below */}
        {step === 1 && (
          <>
            <MarketplaceAccessPanel variant="default" className="mb-6" pasteTarget="below" />

            <div className="container-card p-6 space-y-6 border-2 border-orange-200 bg-gradient-to-b from-orange-50/80 to-white shadow-lg shadow-orange-500/10">
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-orange-600" />
                <h2 className="text-lg font-bold text-slate-900">Paste your product link below</h2>
              </div>

            <MarketplacePlatformPicker
              value={platform}
              onChange={setPlatform}
              className="mb-2"
            />

            <div>
              <Label htmlFor="url">Product URL</Label>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.amazon.com/dp/..."
                  className="flex-1 h-12 text-base"
                  data-testid="input-url"
                />
                <Button
                  onClick={handlePreviewUrl}
                  disabled={loading || !url.trim()}
                  size="lg"
                  className="h-12 min-w-[200px] bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-400 hover:via-amber-400 hover:to-orange-500 text-white font-semibold shadow-lg shadow-orange-500/25"
                  data-testid="preview-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Link2 className="h-5 w-5 mr-2" />
                      Submit Product Link
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Copy the full product URL from the marketplace above and paste it here.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                <strong>Tip:</strong> If the page doesn&apos;t auto-fill, upload an image/screenshot and we&apos;ll handle the rest.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
              <Package className="h-4 w-4 inline mr-2" />
              CheepSweepz is not affiliated with these marketplaces. Support guaranteed.
            </div>
            </div>
          </>
        )}

        {/* Help Request Card - shown on step 1 */}
        {step === 1 && (
          <HelpRequestCard variant="default" className="mt-6" />
        )}

        {/* Step 2: Preview & Edit */}
        {step === 2 && (
          <div className="container-card p-6 space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
              <MarketplaceLogo marketplace={platform} size="lg" />
              <div>
                <p className="font-medium text-slate-900">
                  {PLATFORMS.find((p) => p.id === platform)?.name}
                </p>
                <p className="text-xs text-slate-500 truncate max-w-md">{url}</p>
              </div>
            </div>

            {preview?.has_og_data && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4 inline mr-2" />
                Product details auto-filled from URL. Please review and edit if needed.
              </div>
            )}

            {!preview?.has_og_data && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                Could not auto-fetch details. Please enter the product information manually.
              </div>
            )}

            <div>
              <Label htmlFor="title">Product Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter product title"
                data-testid="input-title"
              />
            </div>

            <div>
              <Label>Product Images * (at least 1 required)</Label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden group">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="Paste image URL"
                  className="flex-1"
                  data-testid="input-image-url"
                />
                <Button variant="outline" onClick={handleAddImage} type="button">
                  <Image className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="select-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="weight">Estimated Weight (lbs, optional)</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={estimatedWeight}
                  onChange={(e) => setEstimatedWeight(e.target.value)}
                  placeholder="0.0"
                  data-testid="input-weight"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Helps us estimate shipping costs
                </p>
              </div>
            </div>

            {/* Note: Pricing is calculated by CheepSweepz, not entered by customer */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
              <Package className="h-4 w-4 inline mr-2" />
              <strong>Note:</strong> The final landed price (including customs, taxes, and shipping) 
              will be calculated by our team after review.
            </div>

            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !title.trim() || images.length === 0}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                data-testid="submit-product-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit for Review"
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="container-card p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Product Submitted!
            </h2>
            <p className="text-slate-600 mb-6">
              Your product is pending review. You can view it in your catalog.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setStep(1);
                setUrl("");
                setPlatform("");
                setTitle("");
                setImages([]);
                setPreview(null);
              }}>
                Submit Another
              </Button>
              <Button onClick={() => navigate("/my-catalog")}>
                View My Catalog
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-slate-500 mt-8 text-center">
          Product images and descriptions are provided for reference only. CheepSweepz is not 
          affiliated with or endorsed by Amazon, eBay, AliExpress, TEMU, SHEIN, Walmart, or Alibaba.
        </p>
      </div>
    </Layout>
  );
}
